import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { CreateUpdateRelistingRuleRequest, IRelistingRule } from "../models/RelistingRule";

export default function useRelistingRules(ruleId?: string) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 10; //10 minutes

  const relistingRules = useQuery({
    queryKey: ["relistingRules"],
    queryFn: () => get<DefaultResponseWithData<IRelistingRule[]>>("/relistingrule"),
    staleTime,
  });

  const ruleCategories = useQuery({
    queryKey: ["relistingRuleCategories", ruleId],
    queryFn: () => get<DefaultResponseWithData<string[]>>(`/relistingrule/${ruleId}/categories`),
    enabled: !!ruleId,
    staleTime,
  });

  const create = useMutation(
    (req: CreateUpdateRelistingRuleRequest) => post(`/relistingrule`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["relistingRules"]);
      },
    }
  );

  const update = useMutation(
    (params: { id: string; req: CreateUpdateRelistingRuleRequest }) =>
      put(`/relistingrule/${params.id}`, params.req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["relistingRules"]);
      },
    }
  );

  const remove = useMutation(
    (id: string) => del(`/relistingrule/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["relistingRules"]);
      },
    }
  );

  const setCategories = useMutation(
    (params: { id: string; categoryIds: string[] }) =>
      put(`/relistingrule/${params.id}/categories`, params.categoryIds),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(["relistingRules"]);
        queryClient.invalidateQueries(["relistingRuleCategories", variables.id]);
      },
    }
  );

  return { relistingRules, ruleCategories, create, update, remove, setCategories };
}


